import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import PageContainer from "../../Components/Page/PageContainer";
import { TabMenu } from "primereact/tabmenu";
import { Card } from "primereact/card";
import { useSelector } from "react-redux";
// import { Dialog } from "primereact/dialog";
// import { Button } from "primereact/button";
// import { Image } from "primereact/image";
import ScorecardTutorial from "./Tutorial/scorecardTutorial";
import { Step } from "react-joyride";
import { useChannelManagerData } from "../../hooks/useChannelManagerData";
import { useVideoReportStatus } from "../../hooks/useVideoReportStatus";
import { ROUTES } from "../../config/routes";

const YouTubeScorecard = () => {
  // const [dialogShown, setDialogShown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Use custom hooks for data management
  const { channelManagerOptionsForFilter, loading: channelManagerLoading } = useChannelManagerData();
  const { videoReportCheck } = useVideoReportStatus();

  // Debug log channel manager options
  console.log("[YouTubeScorecard] channelManagerOptionsForFilter:", channelManagerOptionsForFilter);
  console.log("[YouTubeScorecard] channelManagerLoading:", channelManagerLoading);

  const user = useSelector((state: any) => state.googleAuth.user);

  const userTools: string[] = (() => {
    const raw = user?.roles?.tools;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  })();

  const hasCustomIdLookup = userTools.includes("YouTubeCustomIdLookup");
  const hasReferenceStatus = userTools.includes("YouTubeReferenceStatus");
  const allowedEmails = [
    "matt.napier@moonbug.com",
    "mike.lam@moonbug.com",
    "dane.hagan@moonbug.com",
    "luke.cole@moonbug.com",
    "wojciech.przybylski@moonbug.com",
    "audrey.wouessidjewe@moonbug.com",
    "callum.liu@moonbug.com",
  ];

  // Build ordered tab route list based on visible tabs for the current user
  const buildTabRoutes = useCallback((): string[] => {
    const routes: string[] = [
      ROUTES.YOUTUBE_SCORECARD_METADATA,
      ROUTES.YOUTUBE_SCORECARD_CLAIMS_ADS,
    ];
    if (allowedEmails.includes(user?.email)) {
      routes.push(ROUTES.YOUTUBE_SCORECARD_WHITELIST);
    }
    if (hasCustomIdLookup) {
      routes.push(ROUTES.YOUTUBE_SCORECARD_CUSTOM_ID_LOOKUP);
    }
    if (hasReferenceStatus) {
      routes.push(ROUTES.YOUTUBE_SCORECARD_REFERENCE_STATUS);
    }
    return routes;
  }, [user?.email, hasCustomIdLookup, hasReferenceStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine active tab based on current route
  const getActiveTabIndex = () => {
    const path = location.pathname;
    console.log("Current path:", path);
    if (path.includes("/metadata")) return 0;
    if (path.includes("/claims_ads")) return 1;
    if (path.includes("/whitelist")) {
      return allowedEmails.includes(user?.email) ? 2 : 0;
    }
    if (path.includes("/custom_id_lookup") && hasCustomIdLookup) {
      return buildTabRoutes().indexOf(ROUTES.YOUTUBE_SCORECARD_CUSTOM_ID_LOOKUP);
    }
    if (path.includes("/reference_status") && hasReferenceStatus) {
      return buildTabRoutes().indexOf(ROUTES.YOUTUBE_SCORECARD_REFERENCE_STATUS);
    }
    return 0; // Default to metadata
  };

  const [activeIndex, setActiveIndex] = useState(getActiveTabIndex());

  // Update active tab when route changes
  useEffect(() => {
    const newIndex = getActiveTabIndex();
    console.log("Tab index changed to:", newIndex);
    setActiveIndex(newIndex);
  }, [location.pathname]);

  // Handle tab changes
  const handleTabChange = (e: { index: number }) => {
    console.log("Tab changed to index:", e.index);
    setActiveIndex(e.index);
    const tabRoutes = buildTabRoutes();
    const destination = tabRoutes[e.index] ?? ROUTES.YOUTUBE_SCORECARD_METADATA;
    console.log("Navigating to:", destination);
    navigate(destination);
  };

  // Redirect to metadata tab if on root YouTube Scorecard path
  useEffect(() => {
    if (location.pathname === ROUTES.YOUTUBE_SCORECARD) {
      navigate(ROUTES.YOUTUBE_SCORECARD_METADATA, { replace: true });
    }
  }, [location.pathname, navigate]);

  // Redirect unauthorized users away from whitelist route
  useEffect(() => {
    if (
      location.pathname === ROUTES.YOUTUBE_SCORECARD_WHITELIST &&
      !allowedEmails.includes(user?.email)
    ) {
      navigate(ROUTES.YOUTUBE_SCORECARD_METADATA, { replace: true });
    }
  }, [location.pathname, user?.email, navigate, allowedEmails]);

  // Redirect users without the Custom ID Lookup tool away from that route
  useEffect(() => {
    if (
      location.pathname === ROUTES.YOUTUBE_SCORECARD_CUSTOM_ID_LOOKUP &&
      !hasCustomIdLookup
    ) {
      navigate(ROUTES.YOUTUBE_SCORECARD_METADATA, { replace: true });
    }
  }, [location.pathname, hasCustomIdLookup, navigate]);

  // Redirect users without the Reference Status tool away from that route
  useEffect(() => {
    if (
      location.pathname === ROUTES.YOUTUBE_SCORECARD_REFERENCE_STATUS &&
      !hasReferenceStatus
    ) {
      navigate(ROUTES.YOUTUBE_SCORECARD_METADATA, { replace: true });
    }
  }, [location.pathname, hasReferenceStatus, navigate]);

  const scorecardSteps: Step[] = [
    {
      target: "body",
      content:
        "Welcome to the YouTube: Network Health Check! This tour will guide you through the tool's features.",
      placement: "center",
      title: "Welcome!",
    },
    {
      target: '[data-pc-section="menuitem"]:nth-child(1)',
      content: (
        <div>
          <p>
            Here you can find issues related to video metadata and properties.
          </p>
          <p>
            Key audits for Platfroms include: CustomID | Metadata | MFK | Custom
            Thumbnail
          </p>
        </div>
      ),
      title: "Metadata & Properties Tab",
    },
    {
      target: '[data-pc-section="menuitem"]:nth-child(2)',
      content: (
        <div>
          <p>This tab highlights issues with claims, ads, and monetization.</p>
          <p>
            Key audits for Platfroms include: Third Party Claims | Unclaimed
            Videos
          </p>
        </div>
      ),
      title: "Claims & Ads Tab",
    },
    {
      target: "#audit-filters-container",
      content:
        "You can use these filters to narrow down the results. You can filter by Video ID, CMS, Channel, and more.",
      title: "Filtering Options",
    },
    {
      target: "#issue-treeselect",
      content: "Select specific issues to focus on from this dropdown.",
      title: "Select Issues",
    },
    {
      target: ".custom-datatable-paginator",
      content: (
        <div>
          <p>
            This column shows if a video has a third-party claim. For rows
            marked 'Yes', you can click the 'i' button to see more details.
          </p>
          <img
            src="/third-party-claim-example.png"
            alt="Third-party claim example"
            style={{ width: "100px", marginTop: "10px" }}
          />
        </div>
      ),
      title: "Third-Party Claims",
    },
  ];

  if (allowedEmails.includes(user?.email)) {
    scorecardSteps.splice(3, 0, {
      target: '[data-pc-section="menuitem"]:nth-child(3)',
      content:
        "The whitelist tab is where you can manage rules to exclude certain videos or channels from the scorecard checks.",
      title: "Whitelist Tab",
    });
  }

  const items = [
    { label: "Metadata & Properties", icon: "pi pi-fw pi-tags" },
    { label: "Claims & Ads", icon: "pi pi-fw pi-dollar" },
  ];

  if (allowedEmails.includes(user?.email))
    items.push({ label: "Whitelist", icon: "pi pi-thumbs-up" });

  if (hasCustomIdLookup)
    items.push({ label: "Custom ID Lookup", icon: "pi pi-fw pi-search-plus" });

  if (hasReferenceStatus)
    items.push({ label: "Reference Status", icon: "pi pi-fw pi-info-circle" });



  return (
    <PageContainer
      HomeMenuBarprops={{
        title: "YouTube: Network Health Check",
        Tabs: (
          <div
            id="scorecard-tabs"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <TabMenu
              activeIndex={activeIndex}
              onTabChange={handleTabChange}
              model={items}
            />
            <ScorecardTutorial steps={scorecardSteps} />
          </div>
        ),
      }}
    >
      <div
        className="main-content"
        style={{
          width: "96%",
          margin: "0 auto",
        }}
      >
        <Card
          unstyled
          style={{
            marginTop: "2rem",
            boxShadow: "none",
          }}
        >
          
          {/* Render child routes using Outlet */}
          <Outlet context={{ channelManagerOptionsForFilter }} />
        </Card>
      </div>
    </PageContainer>
  );
};

export default YouTubeScorecard;
